import { DEFAULT_LOCALE, type Locale } from '@diana/shared'

/**
 * Chrome strings for the student area, in the three languages the course
 * teaches in.
 *
 * Unlike the public pages — where copy lives beside each page because Diana
 * edits it — these are short, structural and shared across every screen, so
 * they sit in one file. The lesson content itself is not here: it arrives
 * from the database already carrying PL, UA and RU.
 */

export type StudentStrings = {
  nav: { home: string; courses: string; glossary: string; review: string; account: string }
  signOut: string
  greeting: (name: string) => string
  lessons: {
    title: string
    upcoming: string
    past: string
    none: string
    noneHint: string
    join: string
    status: Record<string, string>
  }
  balance: {
    title: string
    lessonsRemaining: string
    lessonsDone: string
    paid: string
    credit: string
    owed: string
    settled: string
    hint: string
  }
  courses: { title: string; empty: string; emptyHint: string; open: string; continueWith: string }
  course: {
    lessonsDone: string
    nextStep: string
    next: string
    continue: string
    lesson: string
    exercisesSolved: string
    backToCourse: string
  }
  sections: { slownictwo: string; gramatyka: string; czytanie: string; sluchanie: string; 'praca-domowa': string }
  lesson: {
    words: string
    phrases: string
    exercises: string
    audioPending: string
    /** Where a picture the lesson mentions will go, before Diana has uploaded it. */
    figurePending: string
    completed: string
    completeHint: string
    markComplete: string
  }
  exercise: {
    label: string
    check: string
    allRight: string
    tryAgain: string
    correctAnswer: string
    bank: string
    stale: string
    error: string
    writeHere: string
    showModel: string
    showKey: string
    openHint: string
  }
  materials: { title: string; download: string; forLesson: string }
  review: {
    due: string
    learnt: string
    streak: string
    days: string
    empty: string
    emptyHint: string
    nothingDue: string
    sessionDone: (n: number) => string
    comeBack: string
    tapToFlip: string
    again: string
    hard: string
    good: string
    easy: string
    addLesson: string
    added: string
  }
  /** `part`: one piece of a page failed to draw; the rest of the page still works. */
  errors: { load: string; part: string }
}

export const STUDENT_NAV = [
  { key: 'home', to: '/panel', end: true },
  { key: 'review', to: '/panel/powtorki', end: false },
  { key: 'account', to: '/panel/konto', end: false },
] as const satisfies readonly { key: keyof StudentStrings['nav']; to: string; end: boolean }[]

const pl: StudentStrings = {
  nav: { home: 'Strona główna', courses: 'Kursy', glossary: 'Słownik', review: 'Powtórki', account: 'Konto' },
  signOut: 'Wyloguj',
  greeting: (name) => `Cześć, ${name}`,
  lessons: {
    title: 'Twoje lekcje',
    upcoming: 'Najbliższe',
    past: 'Odbyte',
    none: 'Nie masz jeszcze żadnych lekcji.',
    noneHint: 'Kiedy umówisz się z Dianą, termin pojawi się tutaj.',
    join: 'Dołącz',
    status: { confirmed: 'potwierdzona', pending: 'do potwierdzenia', completed: 'odbyta', cancelled: 'odwołana', no_show: 'nieobecność' },
  },
  balance: {
    title: 'Twoje lekcje i płatności',
    lessonsRemaining: 'Lekcje do wykorzystania',
    lessonsDone: 'Lekcje odbyte',
    paid: 'Wpłacono',
    credit: 'Do wykorzystania',
    owed: 'Do dopłaty',
    settled: 'Rozliczone',
    hint: 'Rozliczenia prowadzi Diana — jeśli coś się nie zgadza, napisz do niej.',
  },
  courses: {
    title: 'Twoje kursy',
    empty: 'Nie masz jeszcze żadnego kursu.',
    emptyHint: 'Kurs pojawi się tutaj, kiedy Diana go dla Ciebie otworzy.',
    open: 'Otwórz kurs',
    continueWith: 'Kontynuuj',
  },
  course: {
    lessonsDone: 'lekcji ukończonych',
    nextStep: 'Twój następny krok',
    next: 'następna',
    continue: 'Zaczynaj',
    lesson: 'Lekcja',
    exercisesSolved: 'ćwiczeń',
    backToCourse: 'Kurs',
  },
  sections: {
    slownictwo: 'Słownictwo',
    gramatyka: 'Gramatyka',
    czytanie: 'Czytanie',
    sluchanie: 'Słuchanie',
    'praca-domowa': 'Praca domowa',
  },
  lesson: {
    words: 'Słowa',
    phrases: 'Zwroty',
    exercises: 'Ćwiczenia',
    audioPending: 'Nagranie w przygotowaniu — na razie przeczytaj skrypt poniżej.',
    figurePending: 'Ilustracja w przygotowaniu.',
    completed: 'Lekcja ukończona',
    completeHint: 'Przeszłaś/przeszedłeś całą lekcję? Odhacz ją, a na mapie kursu pojawi się ptaszek.',
    markComplete: 'Oznacz jako ukończoną',
  },
  exercise: {
    label: 'Ćwiczenie',
    check: 'Sprawdź',
    allRight: 'Wszystko dobrze!',
    tryAgain: 'Popraw i sprawdź jeszcze raz.',
    correctAnswer: 'Poprawnie',
    bank: 'Słowa do wyboru',
    stale: 'To ćwiczenie zostało poprawione od Twojego ostatniego podejścia — warto zrobić je jeszcze raz.',
    error: 'Nie udało się sprawdzić. Spróbuj ponownie.',
    writeHere: 'Napisz tutaj…',
    showModel: 'Pokaż wzór',
    showKey: 'Pokaż klucz',
    openHint: 'Tego zadania nie sprawdzi maszyna. Napisz, porównaj ze wzorem — albo wyślij Dianie.',
  },
  materials: { title: 'Materiały do pobrania', download: 'Pobierz PDF', forLesson: 'Lekcja' },
  review: {
    due: 'Do powtórki',
    learnt: 'Nauczone',
    streak: 'Seria',
    days: 'dni',
    empty: 'Nie masz jeszcze żadnych fiszek.',
    emptyHint: 'Wejdź w lekcję i kliknij „Dodaj słowa do powtórek" — słówka z tej lekcji trafią tutaj.',
    nothingDue: 'Na dziś nic do powtórki.',
    sessionDone: (n) => `Gotowe — ${n} ${n === 1 ? 'fiszka' : n < 5 ? 'fiszki' : 'fiszek'}.`,
    comeBack: 'Wróć jutro. System sam podsunie to, co warto odświeżyć.',
    tapToFlip: 'kliknij, żeby zobaczyć odpowiedź',
    again: 'Nie wiem',
    hard: 'Trudne',
    good: 'Dobrze',
    easy: 'Łatwe',
    addLesson: 'Dodaj słowa do powtórek',
    added: 'Słowa są w powtórkach',
  },
  errors: {
    load: 'Nie udało się wczytać. Odśwież stronę.',
    part: 'Tej części nie udało się wyświetlić. Odśwież stronę, a jeśli to nie pomoże — napisz do Diany.',
  },
}

const uk: StudentStrings = {
  nav: { home: 'Головна', courses: 'Курси', glossary: 'Словник', review: 'Повторення', account: 'Обліковий запис' },
  signOut: 'Вийти',
  greeting: (name) => `Привіт, ${name}`,
  lessons: {
    title: 'Твої уроки',
    upcoming: 'Найближчі',
    past: 'Проведені',
    none: 'У тебе ще немає жодного уроку.',
    noneHint: 'Щойно домовишся з Діаною, дата з’явиться тут.',
    join: 'Приєднатися',
    status: { confirmed: 'підтверджений', pending: 'очікує підтвердження', completed: 'проведений', cancelled: 'скасований', no_show: 'неявка' },
  },
  balance: {
    title: 'Твої уроки та оплати',
    lessonsRemaining: 'Уроків залишилось',
    lessonsDone: 'Уроків проведено',
    paid: 'Сплачено',
    credit: 'До використання',
    owed: 'До доплати',
    settled: 'Розраховано',
    hint: 'Розрахунки веде Діана — якщо щось не сходиться, напиши їй.',
  },
  courses: {
    title: 'Твої курси',
    empty: 'У тебе ще немає жодного курсу.',
    emptyHint: 'Курс з’явиться тут, коли Діана відкриє його для тебе.',
    open: 'Відкрити курс',
    continueWith: 'Продовжити',
  },
  course: {
    lessonsDone: 'уроків завершено',
    nextStep: 'Твій наступний крок',
    next: 'наступний',
    continue: 'Почати',
    lesson: 'Урок',
    exercisesSolved: 'вправ',
    backToCourse: 'Курс',
  },
  sections: {
    slownictwo: 'Лексика',
    gramatyka: 'Граматика',
    czytanie: 'Читання',
    sluchanie: 'Аудіювання',
    'praca-domowa': 'Домашнє завдання',
  },
  lesson: {
    words: 'Слова',
    phrases: 'Вирази',
    exercises: 'Вправи',
    audioPending: 'Запис готується — поки що прочитай скрипт нижче.',
    figurePending: 'Ілюстрація готується.',
    completed: 'Урок завершено',
    completeHint: 'Пройшов/пройшла весь урок? Познач його — на карті курсу з’явиться галочка.',
    markComplete: 'Позначити як завершений',
  },
  exercise: {
    label: 'Вправа',
    check: 'Перевірити',
    allRight: 'Усе правильно!',
    tryAgain: 'Виправ і перевір ще раз.',
    correctAnswer: 'Правильно',
    bank: 'Слова на вибір',
    stale: 'Цю вправу виправили після твоєї останньої спроби — варто зробити її ще раз.',
    error: 'Не вдалося перевірити. Спробуй ще раз.',
    writeHere: 'Напиши тут…',
    showModel: 'Показати зразок',
    showKey: 'Показати відповіді',
    openHint: 'Це завдання не перевірить машина. Напиши, порівняй зі зразком — або надішли Діані.',
  },
  materials: { title: 'Матеріали для завантаження', download: 'Завантажити PDF', forLesson: 'Урок' },
  review: {
    due: 'До повторення',
    learnt: 'Вивчено',
    streak: 'Серія',
    days: 'днів',
    empty: 'У тебе ще немає карток.',
    emptyHint: 'Відкрий урок і натисни «Додати слова до повторення» — слова з уроку з’являться тут.',
    nothingDue: 'На сьогодні нічого повторювати.',
    sessionDone: (n) => `Готово — карток: ${n}.`,
    comeBack: 'Повернись завтра. Система сама підкаже, що варто освіжити.',
    tapToFlip: 'натисни, щоб побачити відповідь',
    again: 'Не знаю',
    hard: 'Важко',
    good: 'Добре',
    easy: 'Легко',
    addLesson: 'Додати слова до повторення',
    added: 'Слова вже в повтореннях',
  },
  errors: {
    load: 'Не вдалося завантажити. Онови сторінку.',
    part: 'Цю частину не вдалося показати. Онови сторінку, а якщо не допоможе — напиши Діані.',
  },
}

const ru: StudentStrings = {
  nav: { home: 'Главная', courses: 'Курсы', glossary: 'Словарь', review: 'Повторение', account: 'Аккаунт' },
  signOut: 'Выйти',
  greeting: (name) => `Привет, ${name}`,
  lessons: {
    title: 'Твои уроки',
    upcoming: 'Ближайшие',
    past: 'Проведённые',
    none: 'У тебя пока нет ни одного урока.',
    noneHint: 'Как только договоришься с Дианой, дата появится здесь.',
    join: 'Присоединиться',
    status: { confirmed: 'подтверждён', pending: 'ждёт подтверждения', completed: 'проведён', cancelled: 'отменён', no_show: 'неявка' },
  },
  balance: {
    title: 'Твои уроки и оплаты',
    lessonsRemaining: 'Уроков осталось',
    lessonsDone: 'Уроков проведено',
    paid: 'Оплачено',
    credit: 'К использованию',
    owed: 'К доплате',
    settled: 'Рассчитано',
    hint: 'Расчёты ведёт Диана — если что-то не сходится, напиши ей.',
  },
  courses: {
    title: 'Твои курсы',
    empty: 'У тебя пока нет ни одного курса.',
    emptyHint: 'Курс появится здесь, когда Диана откроет его для тебя.',
    open: 'Открыть курс',
    continueWith: 'Продолжить',
  },
  course: {
    lessonsDone: 'уроков завершено',
    nextStep: 'Твой следующий шаг',
    next: 'следующий',
    continue: 'Начать',
    lesson: 'Урок',
    exercisesSolved: 'упражнений',
    backToCourse: 'Курс',
  },
  sections: {
    slownictwo: 'Лексика',
    gramatyka: 'Грамматика',
    czytanie: 'Чтение',
    sluchanie: 'Аудирование',
    'praca-domowa': 'Домашнее задание',
  },
  lesson: {
    words: 'Слова',
    phrases: 'Выражения',
    exercises: 'Упражнения',
    audioPending: 'Запись готовится — пока прочитай скрипт ниже.',
    figurePending: 'Иллюстрация готовится.',
    completed: 'Урок завершён',
    completeHint: 'Прошёл/прошла весь урок? Отметь его — на карте курса появится галочка.',
    markComplete: 'Отметить как завершённый',
  },
  exercise: {
    label: 'Упражнение',
    check: 'Проверить',
    allRight: 'Всё правильно!',
    tryAgain: 'Исправь и проверь ещё раз.',
    correctAnswer: 'Правильно',
    bank: 'Слова на выбор',
    stale: 'Это упражнение исправили после твоей последней попытки — стоит сделать его ещё раз.',
    error: 'Не удалось проверить. Попробуй ещё раз.',
    writeHere: 'Напиши здесь…',
    showModel: 'Показать образец',
    showKey: 'Показать ответы',
    openHint: 'Это задание не проверит машина. Напиши, сравни с образцом — или отправь Диане.',
  },
  materials: { title: 'Материалы для скачивания', download: 'Скачать PDF', forLesson: 'Урок' },
  review: {
    due: 'К повторению',
    learnt: 'Выучено',
    streak: 'Серия',
    days: 'дней',
    empty: 'У тебя пока нет карточек.',
    emptyHint: 'Открой урок и нажми «Добавить слова к повторению» — слова из урока появятся здесь.',
    nothingDue: 'На сегодня нечего повторять.',
    sessionDone: (n) => `Готово — карточек: ${n}.`,
    comeBack: 'Возвращайся завтра. Система сама подскажет, что стоит освежить.',
    tapToFlip: 'нажми, чтобы увидеть ответ',
    again: 'Не знаю',
    hard: 'Трудно',
    good: 'Хорошо',
    easy: 'Легко',
    addLesson: 'Добавить слова к повторению',
    added: 'Слова уже в повторениях',
  },
  errors: {
    load: 'Не удалось загрузить. Обнови страницу.',
    part: 'Эту часть не удалось показать. Обнови страницу, а если не поможет — напиши Диане.',
  },
}

const BY_LOCALE: Record<Locale, StudentStrings> = { pl, uk, ru }

export function studentCopy(locale: Locale): StudentStrings {
  return BY_LOCALE[locale] ?? BY_LOCALE[DEFAULT_LOCALE]
}
